import { Api } from '@services';
import { useEffect, useState, useCallback } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { PlaceForm } from './PlaceForm';
import { useFormValidation } from './trigger.hooks';

const PAGE_SIZE = 50;

export const PlaceFormContainer = ({
  trigger,
  isSubmitted,
  mapId,
  placeId,
  triggerEnters,
  triggerExits,
  onChange,
  disabled,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // maps options shown in the dropdown (this is now "loaded so far", not "all maps")
  const [maps, setMaps] = useState<any[]>([]);
  const [mapsNextUrl, setMapsNextUrl] = useState<string | null>(null);
  const [mapsQuery, setMapsQuery] = useState<string>('');
  const [isLoadingMaps, setIsLoadingMaps] = useState<boolean>(false);

  const [places, setPlaces] = useState<any[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<any[]>([]);
  const [selectedMap, setSelectedMap] = useState<any[]>([]);
  const [mapIsNull, setMapIsNull] = useState<boolean>(true);

  const [
    conditionError,
    hasName,
    hasType,
    hasCondition,
    hasRisesAbove,
    hasFallsBelow,
    hasEntryExit,
    hasMap,
    hasPlace,
    hasFilter,
    isValid,
  ] = useFormValidation({ trigger });

  // --- normalize maps to a consistent shape ---
  const normalizeMaps = (items: any[]) =>
    (items ?? []).map((m) => ({
      mapId: m.mapId ?? m.id,
      name: m.name,
    }));

  /**
   * Fetch maps
   * - mode: "replace" (new search) OR "append" (paginate)
   * - if nextUrl is provided, we use it directly (returned by API)
   */
  const fetchMaps = useCallback(
    async ({
      search,
      mode,
      nextUrl,
    }: {
      search: string;
      mode: 'replace' | 'append';
      nextUrl?: string | null;
    }) => {
      setIsLoadingMaps(true);

      try {
        const res = nextUrl
          ? await Api.get<any>(nextUrl)
          : await Api.get<any>('v2/maps', {
              params: {
                applicationId: 1,
                pageSize: PAGE_SIZE,
                page: 1,
                search,
              },
            });

        const data = res?.data ?? {};
        const batch = normalizeMaps(data.value ?? data.items ?? []);
        const next = data.next ?? null;

        setMapsNextUrl(next);

        setMaps((prev) => {
          if (mode === 'replace') return batch;

          // append + dedupe by mapId
          const seen = new Set(prev.map((x) => String(x.mapId)));
          const merged = [...prev];
          for (const item of batch) {
            const key = String(item.mapId);
            if (!seen.has(key)) merged.push(item);
          }
          return merged;
        });
      } catch (ex) {
        console.warn(ex);
      } finally {
        setIsLoadingMaps(false);
      }
    },
    [],
  );

  // Called by AsyncTypeahead when user types
  const onMapSearch = useCallback(
    (query: string) => {
      setMapsQuery(query);
      fetchMaps({ search: query, mode: 'replace' });
    },
    [fetchMaps],
  );

  // Called by AsyncTypeahead when user scrolls to bottom
  const onMapPaginate = useCallback(() => {
    if (!mapsNextUrl || isLoadingMaps) return;
    fetchMaps({ search: mapsQuery, mode: 'append', nextUrl: mapsNextUrl });
  }, [fetchMaps, mapsNextUrl, isLoadingMaps, mapsQuery]);

  // OPTIONAL preload so dropdown isn't empty before typing
  useEffect(() => {
    let isLoaded = true;
    setIsLoading(true);

    const preload = async () => {
      try {
        await fetchMaps({ search: '', mode: 'replace' });
      } finally {
        if (isLoaded) setIsLoading(false);
      }
    };

    preload();

    return () => {
      isLoaded = false;
    };
  }, [fetchMaps]);

  // Places fetch (unchanged)
  useEffect(() => {
    if (mapId === undefined || mapId === null) {
      setMapIsNull(true);
      return;
    }

    setMapIsNull(false);
    let isLoaded = true;

    const fetch = async () => {
      try {
        const result = await Api.get<any>(`/maps/${mapId}/places`);
        if (isLoaded) {
          setPlaces(result.data.items);
        }
      } catch (ex) {
        console.warn(ex);
      }
    };

    fetch();

    return () => {
      isLoaded = false;
    };
  }, [mapId]);

  // Keep selected map/place in sync
  useEffect(() => {
    if (mapId != null && maps.length > 0) {
      const currentMap = maps.find((m) => Number(m.mapId) === Number(mapId));
      if (currentMap) {
        setSelectedMap([{ id: String(currentMap.mapId), name: currentMap.name }]);
      }
    } else {
      // if map is cleared, clear selection
      setSelectedMap([]);
    }
    if (placeId == null) {
      setSelectedPlace([]); //clear the displayed place label
      return;
    }

    if (places.length > 0) {
      const currentPlace = places.find((p) => Number(p.id) === Number(placeId));
      setSelectedPlace(
        currentPlace ? [{ id: String(currentPlace.id), name: currentPlace.name }] : [],
      );
    }
  }, [mapId, placeId, maps, places]);

  const onPlaceFormChange = (value: any, type: any) => {
    onChange(value, type);
  };

  return (
    <>
      <Form.Group as={Row}>
        <Form.Label column sm="2">
          Entry/Exit
          <span className="text-danger"> *</span>
        </Form.Label>

        <Col className="d-inline-flex align-items-center">
          <Form.Check
            inline
            isInvalid={!hasEntryExit && isSubmitted}
            isValid={hasEntryExit && isSubmitted}
            className="is-invalid"
            label="Enters"
            name="entersExits"
            type="radio"
            disabled={disabled}
            defaultChecked={triggerEnters}
            onClick={() => onChange(true, 'Enters', { Exits: false })}
          />
          <Form.Check
            inline
            isInvalid={!hasEntryExit && isSubmitted}
            isValid={hasEntryExit && isSubmitted}
            className="is-invalid"
            label="Exits"
            name="entersExits"
            type="radio"
            disabled={disabled}
            defaultChecked={triggerExits}
            onClick={() => onChange(false, 'Enters', { Exits: true })}
          />
          {!hasEntryExit && isSubmitted ? (
            <div className="invalid-feedback">* Please select In or Not In</div>
          ) : null}
        </Col>
      </Form.Group>

      {isLoading === false && (
        <PlaceForm
          mapId={mapId}
          isSubmitted={isSubmitted}
          selectedMap={selectedMap}
          selectedPlace={selectedPlace}
          hasMap={hasMap}
          hasPlace={hasPlace}
          onPlaceFormChange={onPlaceFormChange}
          mapOptions={maps}
          placesOptions={places}
          disabled={disabled}
          mapIsNull={mapIsNull}
          onMapSearch={onMapSearch}
          onMapPaginate={onMapPaginate}
          isLoadingMaps={isLoadingMaps}
          hasMoreMaps={Boolean(mapsNextUrl)}
        />
      )}
    </>
  );
};
